import { Logger } from '@frontend/logger';
import { DockerImageClient } from '@frontend/version/api';
import { useCallback, useEffect, useState } from 'react';

interface Props {
    currentVersion: string;
    repository: string;
    pollIntervalMs?: number;
}
export const useCacheBusting = (props: Props) => {
    const [remoteVersion, changeRemoteVersion] = useState<string | null>(null);
    const [updateAvailable, changeUpdateAvailable] = useState<boolean>(false);

    useEffect(() => {
        const fetchRemoteVersion = () => {
            DockerImageClient.fetchDockerImages({ repository: props.repository }).then((images) => {
                if (images.count === 0) return;
                const image = images.results[0];
                if (image) changeRemoteVersion(image.tag);
            });
        };

        fetchRemoteVersion();

        if (!props.pollIntervalMs) return;

        const intervalId = setInterval(fetchRemoteVersion, props.pollIntervalMs);
        return () => clearInterval(intervalId);
    }, [props.currentVersion, props.repository, props.pollIntervalMs]);

    useEffect(() => {
        // Strip the trailing revision suffix (e.g. "v26.05.11-1" → "v26.05.11") before
        // comparing, since re-deployments of the same build append "-N" to the Docker tag
        // but the version baked into the build artifact never includes that suffix.
        const baseRemoteVersion = remoteVersion ? remoteVersion.replace(/-\d+$/, '') : null;
        if (baseRemoteVersion && !props.currentVersion.endsWith(baseRemoteVersion)) changeUpdateAvailable(true);
        else changeUpdateAvailable(false);
    }, [remoteVersion]);

    useEffect(() => {
        if (typeof navigator === 'undefined' || !('serviceWorker' in navigator) || !navigator.serviceWorker) return;
        const handleNewContent = (event: MessageEvent) => {
            if (event.data && event.data.type === 'SW_NEW_CONTENT') {
                changeUpdateAvailable(true);
            }
        };
        navigator.serviceWorker.addEventListener('message', handleNewContent);
        return () => {
            navigator.serviceWorker.removeEventListener('message', handleNewContent);
        };
    }, []);

    const update = useCallback(() => {
        Logger.debug('Clearing cache and reloading');
        if (caches) {
            caches.keys().then(function (names) {
                for (const name of names) caches.delete(name);
            });
        }
        if (navigator.serviceWorker && navigator.serviceWorker.controller) {
            navigator.serviceWorker.controller.postMessage({ type: 'SKIP_WAITING' });
        }
        window.location.reload();
    }, []);

    return { updateAvailable, update };
};
